import * as React from 'react';
import styled, { css } from 'styled-components';

import { createUIContext } from '../../internals/createContext';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { Presence } from '../../internals/Presence';
import { Slot } from '../../internals/Slot';
import { useControllable } from '../../internals/useControllable';
import { useId } from '../../internals/useId';
import { getRgbaValue } from '../../tokens/colors';
import { rem } from '../../tokens/spacing';

/**
 * Optional CSS variables (set on `Accordion.Root` `style` or a parent) — fallbacks use theme tokens + axis props.
 *
 * | Variable | Fallback when unset |
 * |----------|---------------------|
 * | `--accordion-trigger-bg` | From `triggerSurface` (`accent` → `--ui-interactive-light`, `plain` → `--ui-surface`) |
 * | `--accordion-trigger-padding-y` / `--accordion-trigger-padding-x` | `13px` / `15px` (at root 10px base) |
 * | `--accordion-content-bg` | From `contentSurface` (`muted` → translucent gray, `plain` → `--ui-surface`) |
 * | `--accordion-content-padding-y` / `--accordion-content-padding-x` | `21px` + `32px` (`plain` default x = `42px`) |
 * | `--accordion-content-max-height` | `550px` |
 *
 * Use `variant="any-string"` on Root for `data-variant` — theme or app CSS can target `[data-variant="…"]`.
 */
export type AccordionTriggerIconPosition = 'start' | 'end';

/** Trigger bar fill: accent (brand-tinted) vs plain (surface). */
export type AccordionTriggerSurface = 'accent' | 'plain';

/** Per-item outer chrome: card (bordered panel) vs flush (stacked, dividers when `grouped`). */
export type AccordionItemChrome = 'card' | 'flush';

/** Panel body: muted translucent vs plain (e.g. white) with wider default horizontal padding. */
export type AccordionContentSurface = 'muted' | 'plain';

const ACCORDION_CONTENT_BG_MUTED = getRgbaValue('#d9d9d9', 0.1);

export type AccordionType = 'single' | 'multiple';

interface AccordionContextValue {
  type: AccordionType;
  value: string | string[];
  onValueChange: (next: string | string[]) => void;
  baseId: string;
  grouped: boolean;
  variant: string | undefined;
  triggerIconPosition: AccordionTriggerIconPosition;
  triggerSurface: AccordionTriggerSurface;
  itemChrome: AccordionItemChrome;
  contentSurface: AccordionContentSurface;
}

const [AccordionProvider, useAccordionContext] =
  createUIContext<AccordionContextValue>('Accordion');

function normalizeCollapsed(
  type: AccordionType,
  value: string | string[] | undefined
): string | string[] {
  if (type === 'single') {
    return typeof value === 'string' ? value : '';
  }
  return Array.isArray(value) ? value : [];
}

export interface AccordionRootProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: AccordionType;
  value?: string | string[];
  defaultValue?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  semanticId?: string;
  children: React.ReactNode;
  /** Stack sections inside one bordered shell (`gap: 0`, outer radius). */
  grouped?: boolean;
  /** Opaque preset hook for theme/app CSS: `data-variant={variant}`. */
  variant?: string;
  /** Chevron + label order (`end` ≈ label row with trailing expand). */
  triggerIconPosition?: AccordionTriggerIconPosition;
  /** Trigger background token lane; override anytime with `--accordion-trigger-bg`. */
  triggerSurface?: AccordionTriggerSurface;
  /** Self-contained bordered item vs flush stack rows. */
  itemChrome?: AccordionItemChrome;
  /** Content panel background lane; override with `--accordion-content-bg` / padding vars. */
  contentSurface?: AccordionContentSurface;
}

const shouldForwardRoot = (prop: string | number): boolean => !['$grouped'].includes(String(prop));

const StyledRoot = styled.div.withConfig({ shouldForwardProp: shouldForwardRoot })<{
  $grouped: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;

  ${({ $grouped }) =>
    $grouped
      ? css`
          gap: 0;
          border: 1px solid var(--ui-border-muted);
          border-radius: var(--ui-radius-md, ${rem(6)});
          overflow: hidden;
        `
      : css`
          gap: ${rem(15)};
        `}
`;

const AccordionRoot = React.forwardRef<HTMLDivElement, AccordionRootProps>(function AccordionRoot(
  {
    type = 'single',
    value: valueProp,
    defaultValue,
    onValueChange,
    semanticId,
    children,
    grouped = false,
    variant,
    triggerIconPosition = 'start',
    triggerSurface = 'accent',
    itemChrome = 'card',
    contentSurface = 'muted',
    ...rest
  },
  ref
) {
  const defaultNorm = normalizeCollapsed(type, defaultValue);
  const [value, setValue] = useControllable<string | string[]>({
    value: valueProp,
    defaultValue: defaultNorm,
    onChange: onValueChange,
  });
  const baseId = useId('accordion');
  const semanticProps = getSemanticAttributes(semanticId);

  const ctx = React.useMemo(
    () => ({
      type,
      value,
      onValueChange: setValue,
      baseId,
      grouped,
      variant,
      triggerIconPosition,
      triggerSurface,
      itemChrome,
      contentSurface,
    }),
    [
      type,
      value,
      setValue,
      baseId,
      grouped,
      variant,
      triggerIconPosition,
      triggerSurface,
      itemChrome,
      contentSurface,
    ]
  );

  return (
    <AccordionProvider value={ctx}>
      <StyledRoot
        ref={ref}
        data-orientation='vertical'
        data-variant={variant}
        $grouped={grouped}
        {...semanticProps}
        {...rest}>
        {children}
      </StyledRoot>
    </AccordionProvider>
  );
});

AccordionRoot.displayName = 'Accordion.Root';

interface ItemContextValue {
  itemValue: string;
  open: boolean;
  triggerId: string;
  contentId: string;
}

const [AccordionItemProvider, useAccordionItemContext] =
  createUIContext<ItemContextValue>('AccordionItem');

export interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  semanticId?: string;
}

const shouldForwardItem = (prop: string | number): boolean =>
  !['$itemChrome', '$grouped'].includes(String(prop));

const StyledItem = styled.div.withConfig({ shouldForwardProp: shouldForwardItem })<{
  $itemChrome: AccordionItemChrome;
  $grouped: boolean;
}>`
  width: 100%;
  box-sizing: border-box;

  ${({ $itemChrome, $grouped }) =>
    $itemChrome === 'card'
      ? css`
          border: 1px solid var(--ui-text-subtle);
          border-radius: var(--ui-radius-md, ${rem(6)});
          overflow: hidden;
        `
      : css`
          border: none;
          border-radius: var(--ui-radius-none, 0);
          overflow: visible;
          ${$grouped &&
          css`
            &:not(:last-child) {
              border-bottom: 1px solid var(--ui-border-muted);
            }
          `}
        `}
`;

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(function AccordionItem(
  { value: itemValue, semanticId, children, ...rest },
  ref
) {
  const { type, value, baseId, itemChrome, grouped } = useAccordionContext();
  const open =
    type === 'single' ? value === itemValue : Array.isArray(value) && value.includes(itemValue);
  const triggerId = `${baseId}-trigger-${itemValue}`;
  const contentId = `${baseId}-content-${itemValue}`;
  const semanticProps = getSemanticAttributes(semanticId);

  const itemCtx = React.useMemo(
    () => ({ itemValue, open, triggerId, contentId }),
    [itemValue, open, triggerId, contentId]
  );

  return (
    <AccordionItemProvider value={itemCtx}>
      <StyledItem
        ref={ref}
        data-state={open ? 'open' : 'closed'}
        $itemChrome={itemChrome}
        $grouped={grouped}
        {...semanticProps}
        {...rest}>
        {children}
      </StyledItem>
    </AccordionItemProvider>
  );
});

AccordionItem.displayName = 'Accordion.Item';

const TriggerRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${rem(8)};
`;

const TriggerLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: ${rem(16)};
  line-height: ${rem(22)};
  font-weight: 500;
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  color: var(--ui-text);
`;

const TriggerIcon = styled.span.attrs({ className: 'material-symbols-outlined' })`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${rem(24)};
  line-height: 1;
  color: var(--ui-text);
`;

const TriggerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${rem(22)};
  height: ${rem(22)};
  padding: 0 ${rem(6)};
  flex-shrink: 0;
  background-color: var(--ui-status-danger);
  color: var(--ui-text-inverse);
  font-size: ${rem(12)};
  font-weight: 600;
  border-radius: var(--ui-radius-lg, ${rem(11)});
`;

const shouldForwardTrigger = (prop: string | number): boolean =>
  !['$triggerSurface', '$itemChrome', '$grouped'].includes(String(prop));

const StyledTrigger = styled.button.withConfig({ shouldForwardProp: shouldForwardTrigger })<{
  $triggerSurface: AccordionTriggerSurface;
  $itemChrome: AccordionItemChrome;
  $grouped: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: var(--accordion-trigger-padding-y, ${rem(13)})
    var(--accordion-trigger-padding-x, ${rem(15)});
  border: none;
  font: inherit;
  text-align: left;
  color: var(--ui-text);
  cursor: pointer;

  ${({ $triggerSurface }) =>
    $triggerSurface === 'accent'
      ? css`
          background: var(--accordion-trigger-bg, var(--ui-interactive-light));
        `
      : css`
          background: var(--accordion-trigger-bg, var(--ui-surface));
        `}

  ${({ $itemChrome, $grouped }) =>
    $itemChrome === 'flush' &&
    !$grouped &&
    css`
      border: 1px solid var(--ui-text-subtle);
      border-radius: var(--ui-radius-md, ${rem(6)});
    `}

  &:focus-visible {
    outline: 2px solid var(--ui-focus-ring);
    outline-offset: ${rem(2)};
  }

  &:disabled,
  &[data-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledContentRoot = styled.div`
  display: grid;
  transition: grid-template-rows 0.2s ease;
  grid-template-rows: 0fr;

  &[data-state='open'] {
    grid-template-rows: 1fr;
  }
`;

const StyledContentInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const StyledContentBody = styled.div<{ $contentSurface: AccordionContentSurface }>`
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  max-height: var(--accordion-content-max-height, ${rem(550)});
  border-top: 1px solid var(--ui-border-muted);

  ${({ $contentSurface }) =>
    $contentSurface === 'plain'
      ? css`
          background: var(--accordion-content-bg, var(--ui-surface));
          padding: var(--accordion-content-padding-y, ${rem(21)})
            var(--accordion-content-padding-x, ${rem(42)});
        `
      : css`
          background: var(--accordion-content-bg, ${ACCORDION_CONTENT_BG_MUTED});
          padding: var(--accordion-content-padding-y, ${rem(21)})
            var(--accordion-content-padding-x, ${rem(32)});
        `}

  &::-webkit-scrollbar {
    width: ${rem(5)};
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: var(--ui-radius-md, ${rem(7)});
  }

  &::-webkit-scrollbar-thumb {
    background: var(--ui-text-subtle);
    border-radius: var(--ui-radius-md, ${rem(7)});
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

export interface AccordionTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  semanticId?: string;
  /** Optional count pill (e.g. unread); shown when value &gt; 0. */
  badgeCount?: number | null;
}

const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  function AccordionTrigger(
    {
      asChild = false,
      semanticId,
      children,
      type = 'button',
      disabled,
      onClick,
      badgeCount,
      ...rest
    },
    ref
  ) {
    const {
      type: accordionType,
      value,
      onValueChange,
      triggerIconPosition,
      triggerSurface,
      itemChrome,
      grouped,
    } = useAccordionContext();
    const { itemValue, open, triggerId, contentId } = useAccordionItemContext();
    const semanticProps = getSemanticAttributes(semanticId);

    const toggle = () => {
      if (accordionType === 'single') {
        onValueChange(open ? '' : itemValue);
      } else {
        const arr = Array.isArray(value) ? [...value] : [];
        const idx = arr.indexOf(itemValue);
        if (idx >= 0) arr.splice(idx, 1);
        else arr.push(itemValue);
        onValueChange(arr);
      }
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented && !disabled) toggle();
    };

    const shared = {
      id: triggerId,
      type,
      'aria-controls': contentId,
      'aria-expanded': open,
      'data-state': open ? 'open' : 'closed',
      disabled,
      onClick: handleClick,
      ...semanticProps,
      ...rest,
    };

    if (asChild) {
      const { type: _t, ...slotProps } = shared;
      return (
        <Slot
          ref={ref as React.Ref<HTMLElement>}
          {...(slotProps as unknown as React.ComponentProps<typeof Slot>)}
        />
      );
    }

    const icon = <TriggerIcon aria-hidden>{open ? 'expand_less' : 'expand_more'}</TriggerIcon>;

    const showBadge = badgeCount != null && badgeCount > 0;

    const startIcon = triggerIconPosition === 'start';

    return (
      <StyledTrigger
        ref={ref}
        {...shared}
        $triggerSurface={triggerSurface}
        $itemChrome={itemChrome}
        $grouped={grouped}>
        <TriggerRow>
          {startIcon ? (
            <>
              {icon}
              {showBadge ? <TriggerBadge aria-hidden>{badgeCount}</TriggerBadge> : null}
              <TriggerLabel>{children}</TriggerLabel>
            </>
          ) : (
            <>
              {showBadge ? <TriggerBadge aria-hidden>{badgeCount}</TriggerBadge> : null}
              <TriggerLabel>{children}</TriggerLabel>
              {icon}
            </>
          )}
        </TriggerRow>
      </StyledTrigger>
    );
  }
);

AccordionTrigger.displayName = 'Accordion.Trigger';

export interface AccordionContentProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
  forceMount?: boolean;
}

const AccordionContent = React.forwardRef<HTMLDivElement, AccordionContentProps>(
  function AccordionContent(
    { semanticId, children, forceMount: _forceMount = false, ...rest },
    ref
  ) {
    const { open, contentId, triggerId } = useAccordionItemContext();
    const { contentSurface } = useAccordionContext();
    const semanticProps = getSemanticAttributes(semanticId);

    return (
      <Presence present={open}>
        <StyledContentRoot
          ref={ref}
          role='region'
          id={contentId}
          aria-labelledby={triggerId}
          data-state={open ? 'open' : 'closed'}
          {...semanticProps}
          {...rest}>
          <StyledContentInner>
            <StyledContentBody $contentSurface={contentSurface}>{children}</StyledContentBody>
          </StyledContentInner>
        </StyledContentRoot>
      </Presence>
    );
  }
);

AccordionContent.displayName = 'Accordion.Content';

export const Accordion = {
  Root: AccordionRoot,
  Item: AccordionItem,
  Trigger: AccordionTrigger,
  Content: AccordionContent,
};
